<script setup lang="ts">
import { LucideInfo } from 'lucide-vue-next'
import type { IDepositRate } from '~/components/entity/deposit/item/types'
import { Slider } from '~/components/ui/slider'
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/tabs'
import { ToggleGroup, ToggleGroupItem } from '~/components/ui/toggle-group'
import { AmountCurrencySelector } from '~/components/feature/amount-currency-selector'
import type { IDepositCalculatorProps } from './types'
import type { IMultiselectOption } from '~/types'
import { FeatureDepositRatesTab } from '~/components/feature/deposit/calculator'
import { ANCHOR_IDS } from '~/lib/constants'
import { useCurrencyFormat } from '~/composables/useCurrencyFormat'

const props = withDefaults(defineProps<IDepositCalculatorProps>(), {
  variant: 'white'
})

const activeTab = ref('calculator')

const currencyList = useCurrencyStore().list

const selectedAmount = ref<number>(0)
const selectedCurrencyCode = ref<string>('')

const currencyOptions = computed<IMultiselectOption[]>(() => {
  const currencies = props.deposit?.currencies || []
  return currencies.map((c) => ({
    label: c.code,
    value: c.code
  }))
})

const grouped = computed(
  () =>
    props.deposit?.rates?.reduce<Record<string, IDepositRate[]>>((acc, rate) => {
      const currency = currencyList.find((c) => c.id === rate.currency_id)
      if (!currency) return acc

      const key = currency.code
      if (!acc[key]) acc[key] = []
      acc[key].push(rate)
      return acc
    }, {}) || {}
)

watch(
  currencyOptions,
  (options) => {
    if (!selectedCurrencyCode.value && options.length) {
      selectedCurrencyCode.value = options[0].value
    }
  },
  { immediate: true }
)

watch(
  selectedCurrencyCode,
  (newCode) => {
    const currency = currencyList.find((c) => c.code === newCode)
    if (!currency) return

    const rates = props.deposit?.rates?.filter((r) => r.currency_id === currency.id)
    if (rates?.length) {
      selectedAmount.value = Math.min(...rates.map((r) => r.amount_from))
    }
    activeTab.value = 'calculator'
  },
  { immediate: true }
)

const showTabs = computed(() => {
  const ratesForSelectedCurrency = grouped.value[selectedCurrencyCode.value] || []
  const isKgs = selectedCurrencyCode.value === 'KGS'
  return isKgs && ratesForSelectedCurrency.length >= 3 && ratesForSelectedCurrency.length <= 6
})

const minAmount = computed(() => {
  const rates = grouped.value[selectedCurrencyCode.value] || []
  if (rates.length === 0) return 0
  return Math.min(...rates.map((r) => r.amount_from))
})

const isAmountBelowMin = computed(() => {
  const amount = selectedAmount.value
  if (!amount || amount <= 0) return true
  if (minAmount.value > 0 && amount < minAmount.value) return true
  return false
})

const MAX_DEPOSIT_AMOUNT = 50000000

const hasAmountSlider = computed(() => minAmount.value > 0)

const KGS_CURRENCY_ID = 1

const currencyId = computed(
  () => useGetCurrencyIdByCode(selectedCurrencyCode.value) || KGS_CURRENCY_ID
)

const periodChips = computed(() => {
  const rates = grouped.value[selectedCurrencyCode.value] || []
  if (rates.length === 0) return []

  const periods = new Set<number>()
  for (const rate of rates) {
    if (rate.period_from && rate.period_from !== rate.period) {
      periods.add(rate.period_from)
    }
    periods.add(rate.period)
  }
  return [...periods].sort((a, b) => a - b)
})

const selectedPeriod = ref<number>(0)

watch(
  periodChips,
  (chips) => {
    if (chips.length && !chips.includes(selectedPeriod.value)) {
      selectedPeriod.value = chips[0]
    }
  },
  { immediate: true }
)

const availableRates = computed(() => grouped.value[selectedCurrencyCode.value] || [])

const findBestRate = (rates: IDepositRate[], period: number): IDepositRate | null => {
  if (!rates.length || !period) return null

  if (rates.length === 1) {
    const rate = rates[0]
    const minPeriod = rate.period_from || rate.period
    return period >= minPeriod && period <= rate.period ? rate : null
  }

  const applicable = rates.filter((rate) => (rate.period_from || rate.period) <= period)
  if (!applicable.length) return null

  return applicable.reduce((best, rate) =>
    (rate.period_from || rate.period) > (best.period_from || best.period) ? rate : best
  )
}

const bestRate = computed(() => findBestRate(availableRates.value, selectedPeriod.value))

const currentRate = computed(() => {
  const v = Number(bestRate.value?.rate)
  return !isNaN(v) ? v : 0
})

const currentEffectiveRate = computed(() => {
  const v = Number(bestRate.value?.effective_rate)
  return !isNaN(v) ? v : 0
})

const { calcIncome } = useDepositIncome()

const calculatedEarnings = computed(() =>
  calcIncome(selectedAmount.value || 0, currentRate.value || 0, selectedPeriod.value || 1)
)

const totalAmount = computed(() => (selectedAmount.value || 0) + calculatedEarnings.value)
</script>

<template>
  <ContentBlock
    :id="ANCHOR_IDS.FEATURE_DEPOSIT_CALCULATOR"
    :variant="props.variant"
    :show-link-icon="true"
    class="FormScrollOffset"
  >
    <div class="mb-3 flex h-[38px] flex-wrap justify-between">
      <Heading :tag="props.headingTag" :class="props.headingClass">{{
        props.headingText || $t('menu.deposits.calculator')
      }}</Heading>
      <Tabs v-if="showTabs" v-model="activeTab" class="hidden md:block">
        <TabsList class="gap-3">
          <TabsTrigger value="calculator">
            {{ $t('feature.deposit.calculator.tab_name') }}
          </TabsTrigger>
          <TabsTrigger value="rates">
            {{ $t('feature.deposit.rates.tab_name') }}
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>

    <div class="mb-6 text-gray-700">
      <p>{{ $t('page.deposits.detail.calculator_description_1') }}</p>
    </div>

    <div class="mb-4 flex flex-col gap-4 md:flex-row md:items-start">
      <div class="w-full md:w-1/2 md:shrink-0">
        <AmountCurrencySelector
          v-model:amount="selectedAmount"
          v-model:currency="selectedCurrencyCode"
          :currency-options="currencyOptions"
          :has-error="isAmountBelowMin"
        />
        <div v-if="hasAmountSlider" class="mt-3">
          <Slider
            :model-value="[selectedAmount || minAmount]"
            :min="minAmount"
            :max="MAX_DEPOSIT_AMOUNT"
            :step="minAmount >= 1000 ? 1000 : 100"
            :aria-label-name="$t('widget.calculators.labels.deposit_amount')"
            @update:model-value="(v) => (selectedAmount = v[0])"
          />
          <div class="mt-1.5 flex justify-between text-xs text-gray-500">
            <span>{{ useCurrencyFormat(minAmount, currencyId) }}</span>
            <span>{{ useCurrencyFormat(MAX_DEPOSIT_AMOUNT, currencyId) }}</span>
          </div>
        </div>
        <div class="mt-1.5 text-sm" :class="isAmountBelowMin ? 'text-red-500' : 'text-gray-500'">
          {{ $t('feature.deposit_calculator.labels.min_amount') }}
          {{ useCurrencyFormat(minAmount || 0, currencyId) }}
        </div>
        <div v-if="periodChips.length && activeTab === 'calculator'" class="mt-4">
          <p class="mb-2 text-base font-semibold text-gray-800">
            {{ $t('feature.deposit_calculator.labels.deposit_term_months') }}
          </p>
          <ToggleGroup
            type="single"
            :model-value="String(selectedPeriod)"
            class="flex-wrap justify-start gap-2"
            @update:model-value="(value) => value && (selectedPeriod = Number(value))"
          >
            <ToggleGroupItem
              v-for="chip in periodChips"
              :key="chip"
              :value="String(chip)"
              class="!rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-blue-400 hover:bg-white hover:text-blue-600 data-[state=on]:border-blue-600 data-[state=on]:bg-blue-600 data-[state=on]:text-white"
            >
              {{ chip }}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <div v-if="activeTab === 'calculator'" class="w-full md:w-1/2">
        <div class="rounded-2xl bg-gray-50 p-6">
          <p class="mb-1 text-sm text-gray-500">
            {{ $t('feature.deposit_calculator.results.your_income') }}
          </p>
          <p class="mb-1 text-4xl font-bold tracking-tight text-gray-900">
            {{ useCurrencyFormat(calculatedEarnings, currencyId) }}
          </p>
          <p class="mb-5 text-sm text-gray-500">
            {{ $t('feature.deposit_calculator.results.total_amount_receivable') }}:
            <span class="whitespace-nowrap">{{ useCurrencyFormat(totalAmount, currencyId) }}</span>
          </p>

          <div class="mb-4 h-px bg-gray-200" />

          <div class="grid grid-cols-2 gap-3">
            <div class="rounded-xl bg-white p-3">
              <p class="mb-1 text-xs text-gray-500">
                {{ $t('widget.card_page.info_items.card_interest_rate') }}
              </p>
              <p class="text-xl font-bold text-blue-600">{{ currentRate.toFixed(2) }}%</p>
            </div>
            <div v-if="currentEffectiveRate" class="rounded-xl bg-white p-3">
              <p class="mb-1 text-xs text-gray-500">
                {{ $t('feature.deposit_calculator.results.effective_rate') }}
              </p>
              <p class="text-xl font-bold text-green-600">
                ⚡ {{ currentEffectiveRate.toFixed(2) }}%
              </p>
            </div>
            <div v-if="deposit.payout_cycle_detail" class="rounded-xl bg-white p-3">
              <p class="mb-1 text-xs text-gray-500">
                {{ $t('feature.deposit_calculator.results.payout_cycle') }}
              </p>
              <p class="text-sm font-semibold text-gray-800">{{ deposit.payout_cycle_detail }}</p>
            </div>
            <div v-if="deposit.paid_to" class="rounded-xl bg-white p-3">
              <p class="mb-1 text-xs text-gray-500">
                {{ $t('feature.deposit_calculator.results.paid_to') }}
              </p>
              <p class="text-sm font-semibold text-gray-800">{{ deposit.paid_to }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="activeTab === 'calculator'"
      class="mt-4 flex items-start gap-2 text-xs text-gray-500"
    >
      <LucideInfo class="min-w-4 text-yellow-400" :size="16" />
      <span>{{ $t('feature.deposit_calculator.disclaimer') }}</span>
    </div>

    <div v-if="showTabs && activeTab === 'rates'">
      <FeatureDepositRatesTab
        :grouped="grouped"
        :amount="selectedAmount"
        :currency-code="selectedCurrencyCode"
      />
    </div>
  </ContentBlock>
</template>
